import { useState, type ReactNode } from "react";
import { EyeIcon, XIcon } from "lucide-react";

import { OwnerAvatars, Row, Text } from "@/components";
import { JobOverviewDialog } from "@jobs/JobOverviewDialog";
import { Button } from "@/components/ui/button";
import { SheetClose } from "@/components/ui/sheet";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useCurrentJobResponse } from "@/hooks/queries/useCurrentJobResponse";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

// Gray chrome bar above the sheet header: close, job link, and the job's
// owner rotation. Everything renders from the already-cached job data.
export const CandidacyJobBanner = ({ action }: { action?: ReactNode }) => {
  /* Context */
  const { companyName, job } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const { data: jobResponse } = useCurrentJobResponse();

  /* React Hooks */
  const { userInitialsMap } = useTaggableUsers();
  const [isJobOverviewOpen, setIsJobOverviewOpen] = useState(false);

  /* Helpers */
  const rotationOwnerIds = jobResponse?.candidacyOwnerRotation?.rotation ?? [];

  /* Handlers */
  const handleOpenJobOverview = () => {
    mixpanelUtils.trackButtonClick(
      "Job Overview",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    setIsJobOverviewOpen(true);
  };

  /* Render */
  const render = () => {
    return (
      <Row className="items-center gap-4 border-b border-slate-200 bg-slate-50 px-6 pb-4 pt-4 lg:px-10">
        <SheetClose
          className="shrink-0"
          // Prevents the close button from being focused when navigating with the keyboard.
          tabIndex={-1}
        >
          <XIcon className="h-4 w-4 text-slate-700" />
        </SheetClose>
        <Row className="min-w-0 flex-1 items-center gap-3 overflow-hidden">
          <Button
            className="group/job-link h-auto min-w-0 justify-start gap-2 overflow-hidden p-0 text-slate-600 hover:text-primary-700 hover:no-underline"
            onClick={handleOpenJobOverview}
            type="button"
            variant="link"
          >
            <Text
              className="min-w-0 truncate text-sm font-normal text-inherit group-hover/job-link:underline"
              variant="secondary"
            >
              {job.title} at {companyName}
            </Text>
            <EyeIcon className="h-3.5 w-3.5 shrink-0" />
          </Button>
        </Row>
        {action}
        {rotationOwnerIds.length > 0 ? (
          <Row className="shrink-0 items-center gap-2">
            <Text
              className="text-sm font-normal text-slate-700"
              variant="secondary"
            >
              Job Owners
            </Text>
            <OwnerAvatars
              ownerUserIds={rotationOwnerIds}
              userInitialsMap={userInitialsMap}
            />
          </Row>
        ) : null}
        <JobOverviewDialog
          companyName={companyName}
          job={job}
          onOpenChange={setIsJobOverviewOpen}
          open={isJobOverviewOpen}
        />
      </Row>
    );
  };

  return render();
};
